import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import type { ListQuery, PageDefinition } from '@/types/page'
import type { AdminRecord } from '@/types/record'
import { ApiError } from '@/utils/errors'
import { request } from '@/services/request'
import { usePrototypeStore } from '@/stores/prototype'
import { useSessionCacheStore } from '@/stores/session-cache'
import { useUiStore, type ListMemory } from '@/stores/ui'
import { useWorkspaceStore } from '@/stores/workspace'

export type TableStatus = 'idle' | 'loading' | 'refreshing' | 'success' | 'empty' | 'error' | 'forbidden' | 'offline'

interface CachedQuery {
  keyword?: string
  filters?: Record<string, unknown>
  page?: number
  pageSize?: number
  sortField?: string
  sortOrder?: 'asc' | 'desc'
  scrollTop?: number
}

export function usePageTable(definition: () => PageDefinition) {
  const store = usePrototypeStore()
  const cache = useSessionCacheStore()
  const ui = useUiStore()
  const workspace = useWorkspaceStore()

  const keyword = ref('')
  const filters = reactive<Record<string, unknown>>({})
  const page = ref(1)
  const pageSize = ref(20)
  const total = ref(0)
  const rows = ref<AdminRecord[]>([])
  const summary = reactive<Record<string, number | string>>({})
  const status = ref<TableStatus>('idle')
  const error = ref('')
  const errorCode = ref('')
  const sortField = ref('')
  const sortOrder = ref<'asc' | 'desc' | ''>('')
  const selectedKeys = ref<string[]>([])
  const mounted = ref(false)
  let controller: AbortController | null = null
  let timer = 0
  let requestSequence = 0

  const loading = computed(() => status.value === 'loading' && rows.value.length === 0)
  const refreshing = computed(() => status.value === 'refreshing')
  const activeFilterCount = computed(() => {
    let count = keyword.value.trim() ? 1 : 0
    for (const filter of definition().filters) {
      if (filter.type === 'keyword') continue
      const value = filters[filter.key]
      if (Array.isArray(value) ? value.filter(Boolean).length : value !== '' && value !== undefined && value !== null) count += 1
    }
    return count
  })

  function initialFilters(saved?: CachedQuery): void {
    for (const key of Object.keys(filters)) delete filters[key]
    for (const filter of definition().filters) {
      if (filter.type === 'keyword') continue
      const savedValue = saved?.filters?.[filter.key]
      if (Array.isArray(savedValue)) filters[filter.key] = [...savedValue]
      else if (filter.type === 'multiselect') filters[filter.key] = []
      else filters[filter.key] = savedValue ?? ''
    }
    keyword.value = saved?.keyword ?? ''
    page.value = saved?.page ?? 1
    pageSize.value = saved?.pageSize ?? 20
    sortField.value = saved?.sortField ?? ''
    sortOrder.value = saved?.sortOrder ?? ''
  }

  function queryObject(): Partial<ListQuery> {
    return {
      keyword: keyword.value.trim(),
      filters: JSON.parse(JSON.stringify(filters)) as ListQuery['filters'],
      page: page.value,
      pageSize: pageSize.value,
      sortField: sortField.value || undefined,
      sortOrder: sortOrder.value || undefined,
      scopeRegionId: ui.dataScopeId || undefined,
    }
  }

  function saveMemory(): void {
    const def = definition()
    const scrollTop = typeof window === 'undefined' ? 0 : Math.max(0, Math.round(window.scrollY))
    const memory = {
      keyword: keyword.value,
      filters: JSON.parse(JSON.stringify(filters)) as ListQuery['filters'],
      page: page.value,
      pageSize: pageSize.value,
      sortField: sortField.value,
      sortOrder: sortOrder.value,
      scrollTop,
    }
    cache.cachePage(def.id, memory)
    ui.rememberList(def.id, memory)
    workspace.saveState(def.id, {
      keyword: keyword.value,
      status: '',
      filters: JSON.parse(JSON.stringify(filters)) as Record<string, string | string[] | number | boolean | undefined>,
      dateStart: '',
      dateEnd: '',
      page: page.value,
      pageSize: pageSize.value,
      sortField: sortField.value,
      sortOrder: sortOrder.value === 'desc' ? 'desc' : 'asc',
      scrollY: scrollTop,
      tableScrollTop: 0,
    })
  }

  async function load(mode: 'initial' | 'refresh' = 'initial'): Promise<void> {
    controller?.abort()
    controller = new AbortController()
    const sequence = ++requestSequence
    const query = queryObject()
    status.value = mode === 'refresh' || rows.value.length ? 'refreshing' : 'loading'
    error.value = ''
    errorCode.value = ''
    try {
      const result = await request(() => store.query(definition(), query), {
        delay: 160,
        signal: controller.signal,
      })
      if (sequence !== requestSequence) return
      rows.value = result.rows
      total.value = result.total
      page.value = result.page
      pageSize.value = result.pageSize
      for (const key of Object.keys(summary)) delete summary[key]
      Object.assign(summary, result.summary)
      status.value = result.rows.length ? 'success' : 'empty'
      saveMemory()
    } catch (caught) {
      // An aborted request is normally superseded by a newer load. Only the
      // latest request is allowed to publish state, otherwise a stale abort
      // would overwrite a fresh result.
      if (sequence !== requestSequence) return
      if (caught instanceof ApiError) {
        if (caught.code === 'cancel') {
          // The fault-injection "cancel" scenario must leave a recoverable
          // surface instead of an endless skeleton with no feedback.
          errorCode.value = 'cancel'
          error.value = '请求已取消，可重试或调整筛选条件'
          if (rows.value.length) {
            status.value = 'success'
            ui.toast('info', '请求已取消', '已保留当前列表内容，可继续筛选或重试。', 3200)
          } else {
            status.value = 'error'
          }
          return
        }
        errorCode.value = caught.code
        error.value = caught.message
        if (caught.code === '403') {
          status.value = 'forbidden'
          selectedKeys.value = []
          return
        }
        if (rows.value.length && mode === 'refresh') {
          status.value = 'success'
          ui.toast('error', '刷新未完成', `${caught.message}，已保留当前列表内容。`, 4200)
        } else {
          status.value = caught.code === 'offline' ? 'offline' : 'error'
          if (!rows.value.length) selectedKeys.value = []
        }
      } else {
        status.value = 'error'
        errorCode.value = '500'
        error.value = '列表加载失败，请稍后重试'
      }
    }
  }

  function reload(): void {
    void load('refresh')
  }

  function scheduleReload(): void {
    window.clearTimeout(timer)
    timer = window.setTimeout(() => {
      if (mounted.value) page.value = 1
      void load('initial')
    }, 280)
  }

  function reset(): void {
    workspace.clearState(definition().id)
    cache.cachePage(definition().id, {})
    initialFilters()
    selectedKeys.value = []
    void load('initial')
  }

  function setPage(next: number): void {
    page.value = next
    selectedKeys.value = []
    void load('refresh')
    window.scrollTo({ top: Math.max(0, document.querySelector('.table-card')?.getBoundingClientRect().top ? window.scrollY + (document.querySelector('.table-card')?.getBoundingClientRect().top ?? 0) - 86 : 0), behavior: 'smooth' })
  }

  function setPageSize(next: number): void {
    pageSize.value = next
    page.value = 1
    void load('refresh')
  }

  function sortBy(field: string): void {
    if (!field) return
    if (sortField.value === field) {
      if (sortOrder.value === 'asc') sortOrder.value = 'desc'
      else if (sortOrder.value === 'desc') {
        sortOrder.value = ''
        sortField.value = ''
      } else {
        sortOrder.value = 'asc'
      }
    } else {
      sortField.value = field
      sortOrder.value = 'asc'
    }
    void load('refresh')
  }

  function refresh(): void {
    void load(rows.value.length ? 'refresh' : 'initial')
  }

  function afterMutation(): void {
    refresh()
  }

  watch(() => filters, () => {
    if (!mounted.value) return
    selectedKeys.value = []
    scheduleReload()
  }, { deep: true })

  watch(keyword, () => {
    if (!mounted.value) return
    selectedKeys.value = []
    scheduleReload()
  })

  watch(() => ui.dataScopeId, () => {
    if (!mounted.value) return
    selectedKeys.value = []
    page.value = 1
    void load('refresh')
  })

  onMounted(() => {
    const def = definition()
    const saved = workspace.hasState(def.id)
      ? workspace.stateFor(def.id)
      : cache.readPage(def.id)
    initialFilters(saved as CachedQuery)
    mounted.value = true
    void load('initial').then(() => {
      const scrollTop = (saved as { scrollTop?: number; scrollY?: number } | undefined)?.scrollTop ?? (saved as { scrollY?: number } | undefined)?.scrollY ?? 0
      if (scrollTop > 0) {
        requestAnimationFrame(() => window.scrollTo({ top: scrollTop, behavior: 'auto' }))
      }
    })
  })

  onBeforeUnmount(() => {
    if (mounted.value) saveMemory()
    requestSequence += 1
    controller?.abort()
    window.clearTimeout(timer)
    mounted.value = false
  })

  return {
    keyword,
    filters,
    page,
    pageSize,
    total,
    rows,
    summary,
    status,
    error,
    errorCode,
    sortField,
    sortOrder,
    selectedKeys,
    loading,
    refreshing,
    activeFilterCount,
    load,
    reload,
    reset,
    setPage,
    setPageSize,
    sortBy,
    refresh,
    afterMutation,
    saveMemory,
    queryObject,
  }
}
